import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatSecondsShort } from '../../utils/audio-segments';
import {
  BADGE_COLORS,
  findActiveSlideIndex,
  SlidePanelItem,
  SlideSection,
} from '../../utils/slide-panel.utils';
import styles from './video-analytics.module.scss';

type Props = {
  sections: SlideSection[];
  currentTime: number;
  getThumbUrl: (slideIndex: number) => string;
  onSeek: (seconds: number) => void;
  slidesAnalyzing?: boolean;
  onAnalyzeSlides?: () => void;
};

const SlidePanel: React.FC<Props> = ({
  sections,
  currentTime,
  getThumbUrl,
  onSeek,
  slidesAnalyzing = false,
  onAnalyzeSlides,
}) => {
  const active = useMemo(
    () => findActiveSlideIndex(sections, currentTime),
    [sections, currentTime],
  );

  const sectionIdsKey = useMemo(
    () => sections.filter(s => s.slides.length > 0).map(s => s.id).join(','),
    [sections],
  );

  const [openIds, setOpenIds] = useState<Set<number>>(() => new Set(
    sections.filter(s => s.slides.length > 0).map(s => s.id),
  ));

  useEffect(() => {
    const ids = sectionIdsKey
      ? sectionIdsKey.split(',').map(id => Number(id))
      : [];
    setOpenIds(new Set(ids));
  }, [sectionIdsKey]);

  const toggle = useCallback((id: number) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (!sections.length) {
    return (
      <div className={styles.slidePanelEmpty}>
        <div className={styles.slidePanelEmptyInner}>
          <p className={styles.slidePanelEmptyTitle}>Слайды и экран</p>
          <p className={styles.slidePanelEmptyText}>
            {slidesAnalyzing
              ? 'Идёт VLM-анализ: извлечение кадров и распознавание презентаций (5–15 мин)…'
              : 'Видео прикреплено, но слайды ещё не извлечены. Запустите анализ — появятся миниатюры и таймкоды.'}
          </p>
          {onAnalyzeSlides && (
            <button
              type="button"
              className={styles.slidePanelAnalyzeBtn}
              disabled={slidesAnalyzing}
              onClick={onAnalyzeSlides}
            >
              {slidesAnalyzing ? 'Анализ…' : 'Запустить анализ слайдов'}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.slidePanel}>
      <div className={styles.slidePanelHeader}>Панель со слайдами</div>
      <div className={styles.slidePanelSections}>
        {sections.map(section => {
          const hasSlides = section.slides.length > 0;
          const isOpen = hasSlides && openIds.has(section.id);
          return (
            <div
              key={section.id}
              className={`${styles.slideSection} ${!hasSlides ? styles.slideSectionEmpty : ''}`}
            >
              <button
                type="button"
                className={styles.slideSectionHead}
                onClick={() => toggle(section.id)}
                aria-expanded={isOpen}
                disabled={!hasSlides}
              >
                <span className={styles.slideSectionChevron}>
                  {!hasSlides ? '▸' : isOpen ? '▾' : '▸'}
                </span>
                <span className={styles.slideSectionTitle}>{section.title}</span>
                {hasSlides && (
                  <span className={styles.slideSectionMeta}>{section.slides.length} сл.</span>
                )}
              </button>

              {isOpen && (
                <div className={styles.slideSectionBody}>
                  {section.slides.map((slide: SlidePanelItem) => {
                    const isActive = active?.sectionId === section.id
                      && active.slideIndex === slide.slide_index;
                    return (
                      <button
                        key={`${section.id}-${slide.slide_index}`}
                        type="button"
                        className={`${styles.slidePanelCard} ${isActive ? styles.slidePanelCardActive : ''}`}
                        onClick={() => onSeek(slide.ts)}
                      >
                        <div className={styles.slidePanelThumb}>
                          <img
                            src={getThumbUrl(slide.slide_index)}
                            alt=""
                            onError={e => { (e.target as HTMLImageElement).style.opacity = '0.25'; }}
                          />
                          {slide.badgeType && (
                            <span
                              className={styles.slidePanelStripe}
                              style={{ backgroundColor: BADGE_COLORS[slide.badgeType] }}
                            />
                          )}
                        </div>
                        <div className={styles.slidePanelText}>
                          <span className={styles.slidePanelCardTitle}>{slide.title}</span>
                          <span className={styles.slidePanelCaption}>{slide.caption}</span>
                          <span className={styles.slidePanelTime}>
                            {slide.timestamp || formatSecondsShort(slide.ts)}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SlidePanel;
